import type { ChatAccess, PlayerId, RoomPlayerInfo } from '@pixelworld/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/Button';
import { da } from '../../i18n/da';
import type { RoomFriendsList } from '../friends/RoomFriendControls';
import { CoinCounter, type WalletUpdates } from '../wallet/CoinCounter';
import { PlayerList } from './PlayerList';
import styles from './RoomHud.module.css';

const HINT_VISIBLE_MS = 6000;

/**
 * Room code, players and controls drawn on top of the game, with the chat (`children`) in the
 * same bar: between them on a wide screen, on a row of its own on a phone. Being one bar,
 * nothing in it can lie on top of anything else.
 */
export function RoomHud({
  code,
  showCode,
  players,
  selfId,
  chatAccess,
  onRevokeChat,
  onReport,
  friends,
  wallet,
  children,
}: {
  code: string;
  /**
   * Only rooms joined with the code show it (2026-10-27): anyone can join an open room from
   * the list, so there the code would only take up the screen.
   */
  showCode: boolean;
  players: RoomPlayerInfo[];
  selfId: PlayerId;
  /** Only in public rooms (ADR 0011). */
  chatAccess: ChatAccess | null;
  onRevokeChat: (playerId: PlayerId) => void;
  onReport: (playerId: PlayerId) => Promise<boolean>;
  friends: RoomFriendsList;
  wallet: WalletUpdates;
  children?: ReactNode;
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [isTouch] = useState(() => window.matchMedia('(pointer: coarse)').matches);

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), HINT_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked; the code is visible on screen anyway.
    }
  };

  return (
    <>
      <div className={styles.topBar}>
        {showCode && (
          <div className={styles.chip}>
            <span className={styles.chipLabel}>{da.play.roomCode}</span>
            <strong className={styles.code} data-testid="room-code">
              {code}
            </strong>
            <Button size="small" variant="quiet" onClick={() => void copyCode()}>
              {/* A phone shows only the icon; the words stay for screen readers. */}
              {copied ? <CheckIcon /> : <CopyIcon />}
              <span className={styles.buttonText}>{copied ? da.play.copied : da.play.copy}</span>
            </Button>
          </div>
        )}

        <div className={styles.middle}>{children}</div>

        <div className={styles.right}>
          <CoinCounter updates={wallet} />
          <PlayerList
            players={players}
            selfId={selfId}
            chatAccess={chatAccess}
            onRevoke={onRevokeChat}
            onReport={onReport}
            friends={friends}
          />
          <Button size="small" variant="quiet" onClick={() => void navigate('/')}>
            <LeaveIcon />
            <span className={styles.buttonText}>{da.play.leave}</span>
          </Button>
        </div>
      </div>

      {showHint && (
        <p className={styles.hint}>{isTouch ? da.play.touchHint : da.play.keyboardHint}</p>
      )}
    </>
  );
}

/** Two sheets of paper. */
function CopyIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 20 20" aria-hidden="true">
      <rect x="7" y="7" width="10" height="11" rx="2" />
      <path d="M13 4.5V4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10.5l4 4 8-9" />
    </svg>
  );
}

/** An arrow out of a door. */
function LeaveIcon() {
  return (
    <svg className={styles.icon} viewBox="0 0 20 20" aria-hidden="true">
      <path d="M8 3H4.5A1.5 1.5 0 0 0 3 4.5v11A1.5 1.5 0 0 0 4.5 17H8" />
      <path d="M8 10h9M13.5 6.5L17 10l-3.5 3.5" />
    </svg>
  );
}
